import { Log } from '@artup/utilities'
import { ApplicationFlags } from '@artup/model'
import { APIClient } from './api-client'

/**
 * Common API client
 */
export class CommonAPIClient extends APIClient {
  /**
   * Returns API name served by this client
   */
  get name () {
    return 'Common'
  }

  /**
   * Retrieves client application configuration
   */
  async getClientConfiguration () {
    try {
      const url = this.endpoint('configuration')
      const { configuration, flags, environment, enumerations } = await this.request({ url })
      return {
        configuration,
        environment,
        flags: new ApplicationFlags(flags),
        enumerations: enumerations || configuration?.enumerations || {}
      }
    } catch (error) {
      Log.error('Error retrieving client configuration')
      Log.exception(error)
    }
  }

  /**
   * Retrieves all enumerations or filtered by type
   * @param {Object} [params]
   * @param {String} [params.type] Enumeration type (e.g. 'exhibition')
   * @param {Number} [params.limit] Max records
   * @param {Number} [params.offset] Offset
   * @returns {Promise<{ count: number, items: Array }>}
   */
  async getEnumerations ({ type, limit, offset } = {}) {
    const url = this.endpoint('enumeration')
    const query = {}
    if (type) query.type = type
    if (limit != null) query.limit = limit
    if (offset != null) query.offset = offset
    return this.get({ url, query })
  }

  /**
   * Retrieves a single enumeration by id
   * @param {Number|String} id
   * @returns {Promise<Object>}
   */
  async getEnumeration (id) {
    if (id == null) throw new Error('Enumeration identifier required')
    const url = this.endpoint(`enumeration/${id}`)
    return this.get({ url })
  }

  /**
   * Creates a new enumeration (Requires administrator or integration account)
   * @param {Object} enumeration
   * @returns {Promise<Object>}
   */
  async createEnumeration (enumeration) {
    if (!enumeration) throw new Error('Enumeration data required')
    const url = this.endpoint('enumeration')
    return this.post({ url, data: enumeration })
  }

  /**
   * Updates an existing enumeration (Requires administrator or integration account)
   * @param {Number|String} id
   * @param {Object} enumeration
   * @returns {Promise<Object>}
   */
  async updateEnumeration (id, enumeration) {
    if (id == null) throw new Error('Enumeration identifier required')
    if (!enumeration) throw new Error('Enumeration update data required')
    const url = this.endpoint(`enumeration/${id}`)
    return this.put({ url, data: enumeration })
  }

  /**
   * Deletes an enumeration (Requires administrator or integration account)
   * @param {Number|String} id
   * @returns {Promise<{ success: boolean, id: number }>}
   */
  async deleteEnumeration (id) {
    if (id == null) throw new Error('Enumeration identifier required')
    const url = this.endpoint(`enumeration/${id}`)
    return this.delete({ url })
  }

  /**
   * Pushes a client notification via Common API
   * @param {Object} options Notification options
   * @param {Array<String>|String} [options.accounts] Target user account(s)
   * @param {String} [options.account] Single target account alias
   * @param {String} options.text Markdown formatted notification text
   * @param {String} [options.message] Alias for text
   * @param {String} [options.sender] Package/service that sent the message
   * @param {String} [options.senderDescription] User-friendly description of sender
   * @param {String} [options.description] User-friendly description or title
   * @param {String} [options.level] Level: 'info' | 'success' | 'warning' | 'error'
   * @returns {Promise<Object>}
   */
  async pushNotification ({ accounts, account, text, message, sender, senderDescription, description, level } = {}) {
    const url = this.endpoint('notification/push')
    return this.post({
      url,
      data: {
        accounts,
        account,
        text,
        message,
        sender,
        senderDescription,
        description,
        level
      }
    })
  }
}
